import React, { useState } from 'react';
import ApiClient from "../../utils/ApiClient";
import { getUserName } from "../../auth";

export default function LineItemRow({ item, onResolved }) {
    const [showPriceActions, setShowPriceActions] = useState(false);
    const [showSkuActions, setShowSkuActions] = useState(false);
    const [showCreditInput, setShowCreditInput] = useState(false);
    const [notes, setNotes] = useState('');
    const [creditId, setCreditId] = useState('');
    const [loading, setLoading] = useState(false);

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const hasPriceIssue = !item.poPriceMatched && !item.resolved;
    const hasSkuIssue = !item.poSkuMatched && !item.skuResolutionType;
    const isPendingCredit = item.resolutionType === 'invoice_incorrect' && !item.resolved;

    const handleResolvePrice = async (resolutionType) => {
        try {
            setLoading(true);
            await ApiClient.post(`/api/purchasing/items/${item.itemId}/resolve-discrepancy`, {
                resolutionType,
                notes,
                acceptedBy: getUserName()
            });
            setShowPriceActions(false);
            setNotes('');
            onResolved();
        } catch (error) {
            console.error('Error resolving discrepancy:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveSku = async (skuResolutionType) => {
        try {
            setLoading(true);
            const correctSku = skuResolutionType === 'use_invoice_sku' ? item.sku : null;
            await ApiClient.post(`/api/purchasing/items/${item.itemId}/resolve-sku`, {
                skuResolutionType,
                correctSku,
                notes,
                acceptedBy: getUserName()
            });
            setShowSkuActions(false);
            setNotes('');
            onResolved();
        } catch (error) {
            console.error('Error resolving SKU:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignCredit = async () => {
        if (!creditId.trim()) return;
        try {
            setLoading(true);
            await ApiClient.post(`/api/purchasing/items/${item.itemId}/assign-credit`, {
                creditId,
                notes,
                acceptedBy: getUserName()
            });
            setShowCreditInput(false);
            setCreditId('');
            setNotes('');
            onResolved();
        } catch (error) {
            console.error('Error assigning credit:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <tr className={`${hasPriceIssue || hasSkuIssue ? 'bg-red-50' : ''}`}>
                <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-900">
                    {item.invoiceNumber}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                    <div>{item.sku}</div>
                    {item.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">{item.description}</div>
                    )}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                    {item.poQtyOrdered || '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                    {formatCurrency(item.poUnitCost)}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                    {item.invQtyShipped || '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 text-right">
                    {formatCurrency(item.invUnitPrice)}
                </td>
                <td className={`whitespace-nowrap px-3 py-3 text-sm text-right ${
                    item.variance > 0 ? 'text-red-600 font-medium' : item.variance < 0 ? 'text-green-600' : 'text-gray-500'
                }`}>
                    {formatCurrency(item.variance)}
                    {item.variancePercent != null && item.variancePercent !== 0 && (
                        <div className="text-xs">({item.variancePercent.toFixed(1)}%)</div>
                    )}
                </td>
                {/* Price Check */}
                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                    {item.poPriceMatched ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            ✓ Match
                        </span>
                    ) : item.resolved ? (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            Resolved
                        </span>
                    ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            ✗ Mismatch
                        </span>
                    )}
                </td>
                {/* SKU Check */}
                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                    {item.poSkuMatched ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            ✓ Match
                        </span>
                    ) : item.skuResolutionType ? (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            Resolved
                        </span>
                    ) : (
                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            ✗ Mismatch
                        </span>
                    )}
                </td>
                {/* Unit Check - compare ordered vs received */}
                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                    {(() => {
                        const ordered = item.poQtyOrdered || 0;
                        const received = item.qtyReceived || 0;

                        if (received === 0) {
                            return (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                    Not Received
                                </span>
                            );
                        }

                        const diff = received - ordered;

                        if (diff === 0) {
                            return (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    ✓ {received}/{ordered}
                                </span>
                            );
                        } else if (diff > 0) {
                            return (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                    {received}/{ordered} (+{diff})
                                </span>
                            );
                        } else {
                            return (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                                    {received}/{ordered} ({diff})
                                </span>
                            );
                        }
                    })()}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-sm text-center">
                    <div className="flex justify-center space-x-1">
                        {hasPriceIssue && (
                            <button
                                onClick={() => setShowPriceActions(!showPriceActions)}
                                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                disabled={loading}
                            >
                                Resolve Price
                            </button>
                        )}
                        {hasSkuIssue && (
                            <button
                                onClick={() => setShowSkuActions(!showSkuActions)}
                                className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                                disabled={loading}
                            >
                                Resolve SKU
                            </button>
                        )}
                        {isPendingCredit && (
                            <button
                                onClick={() => setShowCreditInput(!showCreditInput)}
                                className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                                disabled={loading}
                            >
                                Assign Credit
                            </button>
                        )}
                    </div>
                </td>
            </tr>

            {/* Price Resolution Actions */}
            {showPriceActions && (
                <tr className="bg-gray-50">
                    <td colSpan="11" className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-700">Resolve Price Discrepancy:</span>
                            <input
                                type="text"
                                placeholder="Notes (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="flex-1 max-w-xs rounded-md border-gray-300 shadow-sm text-sm"
                            />
                            <button
                                onClick={() => handleResolvePrice('po_incorrect')}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                disabled={loading}
                            >
                                PO Incorrect, Use Invoice
                            </button>
                            <button
                                onClick={() => handleResolvePrice('invoice_incorrect')}
                                className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                                disabled={loading}
                            >
                                Invoice Incorrect, Request Credit
                            </button>
                            <button
                                onClick={() => setShowPriceActions(false)}
                                className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </td>
                </tr>
            )}

            {/* SKU Resolution Actions */}
            {showSkuActions && (
                <tr className="bg-gray-50">
                    <td colSpan="11" className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-700">Resolve SKU Mismatch:</span>
                            <input
                                type="text"
                                placeholder="Notes (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="flex-1 max-w-xs rounded-md border-gray-300 shadow-sm text-sm"
                            />
                            <button
                                onClick={() => handleResolveSku('use_invoice_sku')}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                disabled={loading}
                            >
                                Use Invoice SKU
                            </button>
                            <button
                                onClick={() => handleResolveSku('use_po_sku')}
                                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                disabled={loading}
                            >
                                Use PO SKU
                            </button>
                            <button
                                onClick={() => setShowSkuActions(false)}
                                className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </td>
                </tr>
            )}

            {/* Credit Assignment */}
            {showCreditInput && (
                <tr className="bg-gray-50">
                    <td colSpan="11" className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-700">Assign Credit:</span>
                            <input
                                type="text"
                                placeholder="Credit ID"
                                value={creditId}
                                onChange={(e) => setCreditId(e.target.value)}
                                className="w-40 rounded-md border-gray-300 shadow-sm text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Notes (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="flex-1 max-w-xs rounded-md border-gray-300 shadow-sm text-sm"
                            />
                            <button
                                onClick={handleAssignCredit}
                                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                disabled={loading || !creditId.trim()}
                            >
                                Confirm Credit
                            </button>
                            <button
                                onClick={() => setShowCreditInput(false)}
                                className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
